import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowRight, Package, Wallet as WalletIcon } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { tierIcon, tierTint } from '@/lib/tiers'
import { money } from '@/lib/money'
import { useAuth } from '@/context/AuthContext'
import type { Order, Paginated, WalletInfo } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import Skeleton from '@/components/ui/Skeleton'

export default function AgentDashboard() {
  const { user } = useAuth()

  const wallet = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get<WalletInfo>('/wallet')).data,
  })

  const orders = useQuery({
    queryKey: ['orders', 1],
    // outstanding: what the customer owes on orders produced on credit.
    queryFn: async () => (await api.get<Paginated<Order> & { outstanding?: number }>('/orders')).data,
  })

  const recent = orders.data?.data.slice(0, 5) ?? []
  const TierIcon = tierIcon(wallet.data?.tier?.icon)

  return (
    <div>
      <PageHeader
        title={`Hi, ${user?.name?.split(' ')[0]}`}
        subtitle={user?.company_name ?? 'My account'}
        actions={
          <Link to="/products">
            <Button>
              New order <ArrowRight className="size-4" />
            </Button>
          </Link>
        }
      />

      {/* Phones: wallet full width, tier and orders side by side under it. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <Card className="col-span-2 sm:col-span-1">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-full border border-primary/60 bg-primary/20 text-primary">
              <WalletIcon className="size-5" />
            </span>
            <div>
              <p className="text-2xs font-bold uppercase text-muted">Wallet balance</p>
              {wallet.isLoading ? (
                <Skeleton className="mt-1 h-6 w-24" />
              ) : (
                <p className="text-lg font-bold">{money(wallet.data?.balance)}</p>
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-2 sm:gap-3">
            <span
              className="flex size-8 shrink-0 items-center justify-center rounded-full border sm:size-10"
              style={tierTint(wallet.data?.tier?.color)}
            >
              <TierIcon className="size-4 sm:size-5" />
            </span>
            <div>
              <p className="whitespace-nowrap text-2xs font-bold uppercase text-muted">Price tier</p>
              {wallet.isLoading ? (
                <Skeleton className="mt-1 h-6 w-16" />
              ) : (
                <p className="text-lg font-bold">{wallet.data?.tier?.name ?? 'Member'}</p>
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-success/60 bg-success/20 text-success sm:size-10">
              <Package className="size-4 sm:size-5" />
            </span>
            <div>
              <p className="whitespace-nowrap text-2xs font-bold uppercase text-muted">Total orders</p>
              {orders.isLoading ? (
                <Skeleton className="mt-1 h-6 w-10" />
              ) : (
                <p className="text-lg font-bold">{orders.data?.meta?.total ?? orders.data?.data.length ?? 0}</p>
              )}
            </div>
          </div>
        </Card>
      </div>

      {(orders.data?.outstanding ?? 0) > 0 && (
        <p className="mt-4 rounded-lg border border-warning/60 bg-warning/20 p-3 text-sm font-semibold text-warning">
          Payment due: {money(orders.data?.outstanding)} on orders produced on credit. Please settle it with MPG Printing.
        </p>
      )}

      <h2 className="mb-3 mt-8 text-sm font-bold uppercase tracking-wide text-muted">Recent orders</h2>
      <Table columns={['Order', 'Status', 'Production', 'Delivery due', 'Pieces', 'Total']}>
        {recent.length === 0 && <EmptyRow colSpan={6}>No orders yet - place your first order.</EmptyRow>}
        {recent.map((order) => (
          <Row key={order.id}>
            <Cell>
              <Link to={`/account/orders/${order.id}`} className="font-semibold text-brand-600 hover:underline">
                {order.number}
              </Link>
              <p className="text-2xs text-muted">Ordered {formatDate(order.created_at)}</p>
            </Cell>
            <Cell>
              <StateBadge state={order.status} />
            </Cell>
            <Cell>{formatDate(order.production_date)}</Cell>
            <Cell>{formatDate(order.expected_completion_date)}</Cell>
            <Cell>{order.total_pieces} pcs</Cell>
            <Cell className="font-semibold">{money(order.total)}</Cell>
          </Row>
        ))}
      </Table>
    </div>
  )
}
